/**
 * Core Validation Utilities
 *
 * Shared validation functions for form and data validation
 * across web and mobile platforms.
 *
 * @module @prosperia/shared/validators
 */

/**
 * Validate that a required field has a value
 * @param {*} value - The value to check
 * @param {string} fieldName - Name of the field for error message
 * @throws {Error} If value is null, undefined, or empty string
 */
export const validateRequired = (value, fieldName) => {
  if (value === null || value === undefined || value === '') {
    throw new Error(`${fieldName} is required`)
  }
}

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @throws {Error} If email format is invalid
 */
export const validateEmail = (email) => {
  if (!email) return // Allow empty emails
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email)) {
    throw new Error('Invalid email format')
  }
}

/**
 * Validate phone number format (US)
 * @param {string} phone - Phone number to validate
 * @throws {Error} If phone format is invalid
 */
export const validatePhone = (phone) => {
  if (!phone) return // Allow empty phones

  // Clean the phone number and validate
  const cleaned = phone.replace(/\D/g, '')

  // Valid if 10 digits (US) or 11 digits starting with 1
  const isValid = cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'))

  if (!isValid) {
    throw new Error('Invalid phone number format')
  }
}

/**
 * Validate company ID
 * @param {string} companyId - Company ID to validate
 * @throws {Error} If company ID is missing or invalid
 */
export const validateCompanyId = (companyId) => {
  if (!companyId) {
    throw new Error('Company ID is required')
  }
  if (typeof companyId !== 'string') {
    throw new Error('Company ID must be a string')
  }
}

/**
 * Validate an ID field
 * @param {string} id - ID to validate
 * @param {string} fieldName - Name of the field for error message
 * @throws {Error} If ID is missing or invalid
 */
export const validateId = (id, fieldName = 'ID') => {
  if (!id) {
    throw new Error(`${fieldName} is required`)
  }
  if (typeof id !== 'string') {
    throw new Error(`${fieldName} must be a string`)
  }
}

/**
 * Validate that a value is a positive number
 * @param {*} value - Value to validate
 * @param {string} fieldName - Name of the field for error message
 * @throws {Error} If value is not a positive number
 */
export const validatePositiveNumber = (value, fieldName) => {
  if (value !== null && value !== undefined) {
    const num = Number(value)
    if (isNaN(num) || num < 0) {
      throw new Error(`${fieldName} must be a positive number`)
    }
  }
}

/**
 * Validate that a value is a percentage (0-100)
 * @param {*} value - Value to validate
 * @param {string} fieldName - Name of the field for error message
 * @throws {Error} If value is not a valid percentage
 */
export const validatePercentage = (value, fieldName) => {
  if (value !== null && value !== undefined) {
    const num = Number(value)
    if (isNaN(num) || num < 0 || num > 100) {
      throw new Error(`${fieldName} must be a percentage between 0 and 100`)
    }
  }
}

/**
 * Sanitize input by trimming strings
 * @param {*} input - Input to sanitize
 * @returns {*} Sanitized input
 */
export const sanitizeInput = (input) => {
  if (typeof input === 'string') {
    return input.trim()
  }
  return input
}

/**
 * Sanitize an object by trimming all string values
 * @param {Object} obj - Object to sanitize
 * @param {string[]|null} allowedKeys - Optional list of keys to include
 * @returns {Object} Sanitized object
 */
export const sanitizeObject = (obj, allowedKeys = null) => {
  if (!obj || typeof obj !== 'object') return obj

  const sanitized = {}
  const keys = allowedKeys || Object.keys(obj)

  for (const key of keys) {
    if (obj.hasOwnProperty(key)) {
      sanitized[key] = sanitizeInput(obj[key])
    }
  }

  return sanitized
}

/**
 * Validate that a value is an array with minimum length
 * @param {*} value - Value to validate
 * @param {string} fieldName - Name of the field for error message
 * @param {number} minLength - Minimum required length
 * @throws {Error} If value is not an array or too short
 */
export const validateArray = (value, fieldName, minLength = 0) => {
  if (!Array.isArray(value)) {
    throw new Error(`${fieldName} must be an array`)
  }
  if (value.length < minLength) {
    throw new Error(`${fieldName} must have at least ${minLength} items`)
  }
}

/**
 * Validate that a value is an object with required fields
 * @param {*} value - Value to validate
 * @param {string} fieldName - Name of the field for error message
 * @param {string[]} requiredFields - List of required field names
 * @throws {Error} If value is not an object or missing required fields
 */
export const validateObject = (value, fieldName, requiredFields = []) => {
  if (!value || typeof value !== 'object') {
    throw new Error(`${fieldName} must be an object`)
  }

  for (const field of requiredFields) {
    if (!(field in value)) {
      throw new Error(`${fieldName} must have ${field} field`)
    }
  }
}

/**
 * Validate a date string or Date object
 * @param {string|Date} value - Date to validate
 * @param {string} fieldName - Name of the field for error message
 * @throws {Error} If value is not a valid date
 */
export const validateDate = (value, fieldName) => {
  if (!value) return // Allow empty dates

  const date = value instanceof Date ? value : new Date(value)
  if (isNaN(date.getTime())) {
    throw new Error(`${fieldName} must be a valid date`)
  }
}

/**
 * Validate a URL string
 * @param {string} value - URL to validate
 * @param {string} fieldName - Name of the field for error message
 * @throws {Error} If value is not a valid URL
 */
export const validateUrl = (value, fieldName) => {
  if (!value) return // Allow empty URLs

  try {
    new URL(value)
  } catch {
    throw new Error(`${fieldName} must be a valid URL`)
  }
}

/**
 * Validate string length
 * @param {string} value - String to validate
 * @param {string} fieldName - Name of the field for error message
 * @param {Object} options - Validation options
 * @param {number} options.min - Minimum length
 * @param {number} options.max - Maximum length
 * @throws {Error} If string length is out of range
 */
export const validateStringLength = (value, fieldName, { min = 0, max = Infinity } = {}) => {
  if (!value) return // Allow empty strings (use validateRequired for required fields)

  if (typeof value !== 'string') {
    throw new Error(`${fieldName} must be a string`)
  }

  if (value.length < min) {
    throw new Error(`${fieldName} must be at least ${min} characters`)
  }

  if (value.length > max) {
    throw new Error(`${fieldName} must be at most ${max} characters`)
  }
}

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {{ isValid: boolean, errors: string[] }} Validation result with error messages
 */
export const validatePassword = (password) => {
  const errors = []

  if (!password || typeof password !== 'string') {
    return { isValid: false, errors: ['Password is required'] }
  }

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters')
  }

  if (!/[a-zA-Z]/.test(password)) {
    errors.push('Password must contain at least one letter')
  }

  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number')
  }

  return { isValid: errors.length === 0, errors }
}
